import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { loadStripe, type Stripe as StripeJs } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { useAuth } from '../lib/auth';
import { apiFetch, ApiError } from '../lib/api';
import { LegalModal } from '../components/LegalModal';
import {
  getMissingPreVerifyFields,
  type PreVerifyField,
} from '@lexcrm/shared';
import { decideWizardResume } from '../lib/wizard-route';

/**
 * Registration v3 wizard — five-step path replacing CreateFirm + SignIn
 * for new users. Maps 1:1 to apps/web/../lexcrm-mockup/lexcrm_registration_v3.html
 * + lexcrm_payment_page.html, but rewritten in React with our auth + i18n.
 *
 * Flow:
 *   step 0 (about):   marketing welcome → CTA "start"
 *   step 1 (details): firstName, lastName, firm, email, phone, password
 *                     → signUpWithEmail() (Firebase) → POST /auth/email/start
 *   step 2 (verify):  6-digit OTP → POST /auth/email/verify
 *   step 3 (done):    summary + "continue to payment"
 *                     → POST /api/firms (creates Firm + backfills user profile)
 *                     → POST /api/billing/setup-intent (returns clientSecret)
 *   step 4 (payment): Stripe Elements card form → confirmCardSetup()
 *                     → POST /api/billing/subscribe → receipt sub-step
 *
 * Existing-user re-entry: if the user is already authed when this mounts
 * (e.g. signed in but missing profileCompletedAt), we jump to step 1 in
 * "complete" mode — email/password fields hidden, OTP step skipped.
 */

type Plan = 'monthly' | 'annual';

type WizardData = {
  firstName: string;
  lastName: string;
  firmName: string;
  email: string;
  phone: string;
  prefix: string;
  defaultLocale: 'he' | 'en';
  emailVerified: boolean;
};

type BillingConfig =
  | { configured: false }
  | { configured: true; publishableKey: string; currency: string };

type MeShape = {
  user: {
    email: string;
    firstName?: string | null;
    lastName?: string | null;
    phone?: string | null;
    pendingFirmName?: string | null;
    profileCompletedAt?: string | null;
    defaultLocale?: string | null;
  };
  memberships: Array<{
    firm: {
      id: string;
      name?: string | null;
      subscriptionStatus?: string | null;
      stripeSubscriptionId?: string | null;
      trialEndsAt?: string | null;
    };
  }>;
  onboarding?: { complete: boolean; missing: string[] };
};

const PHONE_PREFIXES = ['050', '051', '052', '053', '054', '055', '058'];
const PROGRESS_PCT = ['25%', '50%', '75%', '100%'];

/**
 * Derive a phone prefix + local part from a stored E.164-ish "0541234567"
 * string. Defaults to 054 / "" if the stored value isn't recognisable.
 */
function splitPhone(stored: string | null | undefined): { prefix: string; local: string } {
  if (!stored) return { prefix: '054', local: '' };
  for (const p of PHONE_PREFIXES) {
    if (stored.startsWith(p)) return { prefix: p, local: stored.slice(p.length) };
  }
  return { prefix: '054', local: stored };
}

export function Register() {
  const { t } = useTranslation('register');
  const { user } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState<number>(user ? 1 : 0);
  const [data, setData] = useState<WizardData>({
    firstName: '',
    lastName: '',
    firmName: '',
    email: user?.email ?? '',
    phone: '',
    prefix: '054',
    defaultLocale: 'he',
    emailVerified: !!user, // already-authed users are by definition verified
  });
  const [meLoaded, setMeLoaded] = useState(!user);

  // Stripe publishable key fetched on mount (public endpoint, no auth).
  const [billingConfig, setBillingConfig] = useState<BillingConfig | null>(null);
  const [stripePromise, setStripePromise] = useState<Promise<StripeJs | null> | null>(null);

  useEffect(() => {
    apiFetch<BillingConfig>('/api/billing/config')
      .then((c) => {
        setBillingConfig(c);
        if (c.configured) setStripePromise(loadStripe(c.publishableKey));
      })
      .catch(() => setBillingConfig({ configured: false }));
  }, []);

  // Resume logic — if the user is already signed in, /me tells us how far
  // through the wizard they got and we jump them there. Wizard-state
  // (firstName/lastName/phone/pendingFirmName) is mirrored back from /me.
  useEffect(() => {
    if (!user) {
      setMeLoaded(true);
      return;
    }
    let alive = true;
    apiFetch<MeShape>('/api/me')
      .then((me) => {
        if (!alive) return;
        const phoneSplit = splitPhone(me.user.phone);
        const existingFirmName = me.memberships[0]?.firm?.name ?? null;
        setData((prev) => ({
          ...prev,
          email: me.user.email ?? prev.email,
          firstName: me.user.firstName ?? prev.firstName,
          lastName: me.user.lastName ?? prev.lastName,
          phone: phoneSplit.local || prev.phone,
          prefix: phoneSplit.prefix,
          // Pre-fill firmName from the actual firm row when one already
          // exists — pendingFirmName is cleared at firm-create time, so
          // for an existing-firm user resuming the wizard to fill missing
          // user fields the only source of the firm's name is the firm row.
          firmName: me.user.pendingFirmName ?? existingFirmName ?? prev.firmName,
          defaultLocale: (me.user.defaultLocale === 'en' ? 'en' : 'he') as 'he' | 'en',
          emailVerified: true,
        }));
        // Pick the right step based on where the user got stuck. The
        // decision lives in a pure helper so the gate-vs-wizard
        // no-loop invariant is unit-tested at deploy pre-flight (see
        // apps/web/src/lib/wizard-route.test.ts).
        const decision = decideWizardResume(me);
        if (decision.kind === 'goto') {
          navigate(decision.path);
          return;
        }
        setStep(decision.step);
      })
      .catch(() => {
        // /me unreachable — leave step at default
      })
      .finally(() => alive && setMeLoaded(true));
    return () => {
      alive = false;
    };
  }, [user, navigate]);

  const goNext = useCallback(() => setStep((s) => Math.min(3, s + 1)), []);

  function tabClick(idx: number) {
    // Tabs are clickable for completed steps only (no skipping forward).
    if (idx <= step) setStep(idx);
  }

  if (!meLoaded) {
    return (
      <main className="reg-wrap">
        <p className="reg-step-sub centered">…</p>
      </main>
    );
  }

  return (
    <main className="reg-wrap">
      <header className="reg-brand">
        <div className="reg-brand-name">{t('brand.name')}</div>
        <div className="reg-brand-sub">{t('brand.tagline')}</div>
      </header>

      <div className="reg-prog-bar">
        <div className="reg-prog-fill" style={{ width: PROGRESS_PCT[step] ?? '0%' }} />
      </div>

      <div className="reg-tabs" role="tablist">
        {(['about', 'details', 'done', 'payment'] as const).map((key, idx) => {
          const cls =
            'reg-tab' +
            (idx === step ? ' active' : idx < step ? ' done' : '') +
            (idx <= step ? ' clickable' : '');
          return (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={idx === step}
              className={cls}
              onClick={() => tabClick(idx)}
              disabled={idx > step}
            >
              <span className="reg-tab-num">{idx < step ? '✓' : idx + 1}</span>
              <span className="reg-tab-label">{t(`tabs.${key}`)}</span>
            </button>
          );
        })}
      </div>

      <section className="reg-panel">
        {step === 0 && <StepAbout onNext={goNext} onSignIn={() => navigate('/signin')} />}
        {step === 1 && (
          <StepDetails
            data={data}
            setData={setData}
            onNext={goNext}
          />
        )}
        {step === 2 && (
          <StepDone
            data={data}
            onNext={goNext}
            onSkipPayment={() => navigate('/dashboard')}
            paymentConfigured={billingConfig?.configured === true}
          />
        )}
        {step === 3 && billingConfig?.configured && stripePromise && (
          <Elements stripe={stripePromise} options={{ locale: data.defaultLocale }}>
            <StepPayment
              data={data}
              onDone={() => navigate('/dashboard')}
            />
          </Elements>
        )}
        {step === 3 && billingConfig && !billingConfig.configured && (
          <PaymentNotConfigured onContinue={() => navigate('/dashboard')} />
        )}
      </section>
    </main>
  );
}

/* ------------------------------------------------------------------ Step 0 */

function StepAbout({ onNext, onSignIn }: { onNext: () => void; onSignIn: () => void }) {
  const { t } = useTranslation('register');
  const features = [
    'clients',
    'cases',
    'docs',
    'calendar',
    'billing',
    'gmail',
  ] as const;
  return (
    <div className="reg-step">
      <h2 className="reg-step-title">{t('step_about.title')}</h2>
      <p className="reg-step-sub">{t('step_about.subtitle')}</p>
      <div className="reg-feature-list">
        {features.map((k) => (
          <div className="reg-fi" key={k}>
            <div className="reg-fi-ico" aria-hidden="true">
              <FeatureIcon kind={k} />
            </div>
            <div>
              <div className="reg-fi-t">{t(`step_about.features.${k}_t`)}</div>
              <div className="reg-fi-s">{t(`step_about.features.${k}_s`)}</div>
            </div>
          </div>
        ))}
      </div>
      <div className="reg-info-banner">
        <span aria-hidden="true">ⓘ</span>
        <span>{t('step_about.trial_banner')}</span>
      </div>
      <button type="button" className="btn-primary reg-cta" onClick={onNext}>
        {t('step_about.cta')}
      </button>
      <button type="button" className="btn-link reg-have-account" onClick={onSignIn}>
        {t('step_about.have_account')}
      </button>
    </div>
  );
}

function FeatureIcon({ kind }: { kind: string }) {
  const paths: Record<string, ReactNode> = {
    clients: (
      <>
        <circle cx="9" cy="7" r="4" />
        <path d="M3 21v-2a4 4 0 014-4h4a4 4 0 014 4v2" />
      </>
    ),
    cases: (
      <>
        <path d="M20 7H4a2 2 0 00-2 2v9a2 2 0 002 2h16a2 2 0 002-2V9a2 2 0 00-2-2z" />
        <path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2" />
      </>
    ),
    docs: (
      <>
        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
        <polyline points="14 2 14 8 20 8" />
      </>
    ),
    calendar: (
      <>
        <rect x="3" y="4" width="18" height="18" rx="2" />
        <path d="M16 2v4M8 2v4M3 10h18" />
      </>
    ),
    billing: (
      <>
        <rect x="2" y="5" width="20" height="14" rx="2" />
        <path d="M2 10h20" />
      </>
    ),
    gmail: (
      <>
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
        <polyline points="22,6 12,13 2,6" />
      </>
    ),
  };
  return (
    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2">
      {paths[kind] ?? null}
    </svg>
  );
}

/* ------------------------------------------------------------------ Step 1 */

function StepDetails({
  data,
  setData,
  onNext,
}: {
  data: WizardData;
  setData: (next: WizardData) => void;
  onNext: () => void;
}) {
  const { t } = useTranslation('register');
  const { signInWithGoogle } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [missingFields, setMissingFields] = useState<PreVerifyField[]>([]);
  const [showLegal, setShowLegal] = useState<'terms' | 'privacy' | null>(null);
  const [otpOpen, setOtpOpen] = useState(false);

  const set = <K extends keyof WizardData>(k: K, v: WizardData[K]) => {
    setData({ ...data, [k]: v });
    // Drop the "missing" highlight for the field as soon as the user
    // starts typing in it. They've acknowledged the error.
    if (missingFields.length > 0) {
      setMissingFields((prev) => prev.filter((f) => fieldFromKey(k) !== f));
    }
  };

  function isInvalid(field: PreVerifyField): boolean {
    return missingFields.includes(field);
  }

  /**
   * Validate the form against the shared pre-verify rules. Returns true
   * if all required fields are present (and email is well-formed for
   * the email-code path); otherwise sets `missingFields` so the inputs
   * highlight and `error` for the form-level message.
   */
  function validateForVerify(opts: { requireEmail: boolean }): boolean {
    const missing = getMissingPreVerifyFields({
      firstName: data.firstName,
      firmName: data.firmName,
      phone: data.phone,
    });
    setMissingFields(missing);
    if (missing.length > 0) {
      setError(t('step_details.errors.fill_all'));
      return false;
    }
    if (opts.requireEmail) {
      if (!data.email.trim()) {
        setError(t('step_details.errors.missing_email'));
        return false;
      }
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(data.email.trim())) {
        setError(t('step_details.errors.invalid_email'));
        return false;
      }
    }
    setError(null);
    return true;
  }

  /**
   * Persist the typed wizard fields to the User row. Called after
   * Google or email-code verification succeeds — at that point we have
   * a Firebase session, so PATCH /me will land. The firmName goes to
   * `pendingFirmName` until the firm is actually created in step 2.
   */
  async function persistWizardState() {
    const phone = data.phone ? `${data.prefix}${data.phone}` : '';
    await apiFetch('/api/me', {
      method: 'PATCH',
      body: JSON.stringify({
        firstName: data.firstName.trim(),
        lastName: data.lastName.trim(),
        phone,
        pendingFirmName: data.firmName.trim() || null,
        defaultLocale: data.defaultLocale,
      }),
    });
  }

  async function handleGoogle() {
    // Google ignores the typed email but still requires the rest of
    // the profile, so the same validation gate applies.
    if (!validateForVerify({ requireEmail: false })) return;
    setBusy(true);
    setError(null);
    try {
      await signInWithGoogle();
      const fbAuth = (await import('../lib/firebase')).firebaseAuth;
      const fbUser = fbAuth.currentUser;
      const fbEmail = fbUser?.email ?? '';
      // Google's email wins. Also fill first/last only if the user
      // didn't type them (their typed value is more likely correct,
      // e.g. Hebrew names while the Google profile is Latin).
      const next = { ...data, email: fbEmail || data.email, emailVerified: true };
      const dn = fbUser?.displayName ?? '';
      if (!data.firstName.trim() && dn) {
        const [first, ...rest] = dn.split(/\s+/);
        next.firstName = first ?? '';
        if (!data.lastName.trim()) next.lastName = rest.join(' ');
      }
      setData(next);
      try {
        await persistWizardState();
      } catch {
        /* non-fatal — resume on next page-load handles the rest */
      }
      onNext();
    } catch (err) {
      const msg = (err as Error)?.message ?? '';
      if (/cancel|popup-closed|closed-by-user/i.test(msg)) {
        setError(t('step_details.errors.google_canceled'));
      } else {
        setError(t('step_details.errors.google_failed'));
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleSendCode(e: FormEvent) {
    e.preventDefault();
    if (!validateForVerify({ requireEmail: true })) return;
    setBusy(true);
    try {
      await apiFetch('/api/auth/email/start', {
        method: 'POST',
        body: JSON.stringify({ email: data.email.trim() }),
      });
      setOtpOpen(true);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? t(`step_details.errors.${err.code}`, {
              defaultValue: t('step_details.errors.send_code_failed'),
            })
          : t('step_details.errors.send_code_failed'),
      );
    } finally {
      setBusy(false);
    }
  }

  async function handleOtpSuccess() {
    setOtpOpen(false);
    setData({ ...data, emailVerified: true });
    try {
      await persistWizardState();
    } catch {
      /* non-fatal */
    }
    onNext();
  }

  return (
    <form className="reg-step" onSubmit={handleSendCode} noValidate>
      <h2 className="reg-step-title">{t('step_details.title')}</h2>
      <p className="reg-step-sub">{t('step_details.subtitle')}</p>

      <div className="reg-field-row">
        <label className={`reg-field ${isInvalid('firstName') ? 'invalid' : ''}`}>
          <span>
            {t('step_details.first_name')}
            {t('step_details.required_marker')}
          </span>
          <input
            type="text"
            value={data.firstName}
            onChange={(e) => set('firstName', e.target.value)}
            placeholder={t('step_details.first_name_placeholder')}
            required
            autoComplete="given-name"
          />
        </label>
        <label className="reg-field">
          <span>{t('step_details.last_name')}</span>
          <input
            type="text"
            value={data.lastName}
            onChange={(e) => set('lastName', e.target.value)}
            placeholder={t('step_details.last_name_placeholder')}
            autoComplete="family-name"
          />
        </label>
      </div>

      <label className={`reg-field ${isInvalid('firmName') ? 'invalid' : ''}`}>
        <span>
          {t('step_details.firm_name')}
          {t('step_details.required_marker')}
        </span>
        <input
          type="text"
          value={data.firmName}
          onChange={(e) => set('firmName', e.target.value)}
          placeholder={t('step_details.firm_name_placeholder')}
          required
          minLength={2}
          maxLength={80}
          autoComplete="organization"
        />
      </label>

      <label className={`reg-field ${isInvalid('phone') ? 'invalid' : ''}`}>
        <span>
          {t('step_details.phone')}
          {t('step_details.required_marker')}
        </span>
        <div className="reg-phone-row">
          <select
            className="reg-phone-prefix"
            value={data.prefix}
            onChange={(e) => set('prefix', e.target.value)}
            aria-label="prefix"
          >
            {PHONE_PREFIXES.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
          <input
            type="tel"
            value={data.phone}
            onChange={(e) => set('phone', e.target.value.replace(/\D/g, ''))}
            placeholder={t('step_details.phone_placeholder')}
            inputMode="numeric"
            required
            autoComplete="tel-national"
          />
        </div>
      </label>

      <label className="reg-field">
        <span>
          {t('step_details.email')}
          {t('step_details.required_marker')}
          {data.emailVerified && (
            <span className="reg-email-verified-tag"> {t('step_details.email_verified')}</span>
          )}
        </span>
        <input
          type="email"
          value={data.email}
          onChange={(e) => set('email', e.target.value)}
          placeholder={t('step_details.email_placeholder')}
          required
          autoComplete="email"
          disabled={data.emailVerified}
        />
      </label>

      <label className="reg-field">
        <span>{t('step_details.language')}</span>
        <select
          value={data.defaultLocale}
          onChange={(e) => set('defaultLocale', e.target.value as 'he' | 'en')}
        >
          <option value="he">עברית</option>
          <option value="en">English</option>
        </select>
      </label>

      {error && <div className="reg-error">{error}</div>}

      {data.emailVerified ? (
        <button
          type="button"
          className="btn-primary reg-cta"
          onClick={onNext}
          disabled={busy}
        >
          {t('step_details.submit_after_verify')}
        </button>
      ) : (
        <div className="reg-verify-section">
          <div className="reg-divider"><span>{t('step_details.verify_section_title')}</span></div>
          <button
            type="button"
            className="reg-google-btn"
            onClick={handleGoogle}
            disabled={busy}
          >
            <GoogleGlyph />
            {t('step_details.google_button')}
          </button>
          <button type="submit" className="btn-primary reg-cta" disabled={busy}>
            {busy ? '…' : t('step_details.send_code')}
          </button>
        </div>
      )}

      <p className="reg-terms">
        {t('step_details.terms_prefix')}
        <button type="button" className="btn-link" onClick={() => setShowLegal('terms')}>
          {t('step_details.terms_link')}
        </button>
        {t('step_details.terms_and')}
        <button type="button" className="btn-link" onClick={() => setShowLegal('privacy')}>
          {t('step_details.privacy_link')}
        </button>
      </p>

      {showLegal && (
        <LegalModal initialTab={showLegal} onClose={() => setShowLegal(null)} />
      )}
      {otpOpen && (
        <OtpModal
          email={data.email.trim()}
          onClose={() => setOtpOpen(false)}
          onVerified={handleOtpSuccess}
        />
      )}
    </form>
  );
}

/** Map a WizardData key to the matching pre-verify field name. */
function fieldFromKey(k: keyof WizardData): PreVerifyField | null {
  if (k === 'firstName') return 'firstName';
  if (k === 'firmName') return 'firmName';
  if (k === 'phone') return 'phone';
  return null;
}

function GoogleGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
  );
}

/* ------------------------------------------ OTP modal (inline on step 1) */

function OtpModal({
  email,
  onClose,
  onVerified,
}: {
  email: string;
  onClose: () => void;
  onVerified: () => void;
}) {
  const { t } = useTranslation('register');
  const { signInWithCustom } = useAuth();
  const [code, setCode] = useState<string[]>(Array(6).fill(''));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [seconds, setSeconds] = useState(60);
  const inputs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (seconds <= 0) return;
    const id = setInterval(() => setSeconds((s) => s - 1), 1000);
    return () => clearInterval(id);
  }, [seconds]);

  function setDigit(i: number, v: string) {
    const ch = v.replace(/\D/g, '').slice(-1);
    const next = [...code];
    next[i] = ch;
    setCode(next);
    setError(null);
    if (ch && i < 5) inputs.current[i + 1]?.focus();
  }
  function onKey(e: KeyboardEvent<HTMLInputElement>, i: number) {
    if (e.key === 'Backspace' && !code[i] && i > 0) inputs.current[i - 1]?.focus();
  }

  async function resend() {
    setError(null);
    try {
      await apiFetch('/api/auth/email/start', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
      setSeconds(60);
      setCode(Array(6).fill(''));
      inputs.current[0]?.focus();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? t(`otp_modal.errors.${err.code}`, {
              defaultValue: t('otp_modal.errors.verify_failed'),
            })
          : t('otp_modal.errors.verify_failed'),
      );
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const joined = code.join('');
    if (joined.length < 6) {
      setError(t('otp_modal.errors.incomplete'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch<{ customToken: string }>('/api/auth/email/verify', {
        method: 'POST',
        body: JSON.stringify({ email, code: joined }),
      });
      if (res.customToken) {
        try {
          await signInWithCustom(res.customToken);
        } catch {
          /* already authed; harmless */
        }
      }
      onVerified();
    } catch (err) {
      setError(
        err instanceof ApiError
          ? t(`otp_modal.errors.${err.code}`, {
              defaultValue: t('otp_modal.errors.invalid_code'),
            })
          : t('otp_modal.errors.verify_failed'),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="legal-modal-bg"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="legal-modal" role="dialog" aria-modal="true">
        <header className="legal-modal-hdr">
          <div className="legal-modal-title">{t('otp_modal.title')}</div>
          <button className="legal-modal-close" onClick={onClose} aria-label={t('otp_modal.close')}>
            ✕
          </button>
        </header>
        <form onSubmit={handleSubmit} className="reg-otp-modal-body">
          <p className="reg-step-sub">{t('otp_modal.subtitle', { email })}</p>
          <p className="reg-code-prompt">{t('otp_modal.enter_code')}</p>
          <div className="reg-code-inputs" dir="ltr">
            {code.map((d, i) => (
              <input
                key={i}
                ref={(el) => {
                  inputs.current[i] = el;
                }}
                className={`reg-cd ${d ? 'filled' : ''}`}
                inputMode="numeric"
                type="text"
                maxLength={1}
                value={d}
                onChange={(e) => setDigit(i, e.target.value)}
                onKeyDown={(e) => onKey(e, i)}
                autoFocus={i === 0}
              />
            ))}
          </div>
          {error && <div className="reg-error">{error}</div>}
          <button type="submit" className="btn-primary reg-cta" disabled={busy}>
            {busy ? '…' : t('otp_modal.submit')}
          </button>
          <div className="reg-resend-row">
            {t('otp_modal.resend_prompt')}{' '}
            <button
              type="button"
              className="btn-link"
              onClick={resend}
              disabled={seconds > 0}
            >
              {t('otp_modal.resend_link')}
            </button>{' '}
            {seconds > 0 && (
              <span className="reg-resend-timer">
                {t('otp_modal.resend_timer', { seconds })}
              </span>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ Step 2 */

function StepDone({
  data,
  onNext,
  onSkipPayment,
  paymentConfigured,
}: {
  data: WizardData;
  onNext: () => void;
  onSkipPayment: () => void;
  paymentConfigured: boolean;
}) {
  const { t } = useTranslation('register');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleContinue() {
    setBusy(true);
    setError(null);
    type FirmsResp = {
      firm: {
        subscriptionStatus?: string | null;
        trialEndsAt?: string | null;
        stripeSubscriptionId?: string | null;
      };
    };
    const body = JSON.stringify({
      name: data.firmName.trim(),
      defaultLocale: data.defaultLocale,
      firstName: data.firstName.trim(),
      lastName: data.lastName.trim(),
      phone: `${data.prefix}${data.phone}`,
    });
    /** Whether the firm we got back already has a non-expired plan, so
     *  the wizard should jump the user straight to /dashboard instead
     *  of running them through Stripe again. The stripeSubscriptionId
     *  guard is critical: every freshly-created firm gets an
     *  auto-stamped subscriptionStatus='trialing' + trialEndsAt=+30d
     *  on the server (apps/api/src/routes/firms.ts), so without it
     *  every new user would skip past the payment step. A real,
     *  card-backed trial only exists once /api/billing/subscribe has
     *  filled in stripeSubscriptionId. */
    function alreadyPaying(resp: FirmsResp): boolean {
      const s = resp.firm.subscriptionStatus;
      if (!resp.firm.stripeSubscriptionId) return false;
      if (s === 'active') return true;
      if (s === 'trialing' && resp.firm.trialEndsAt) {
        return new Date(resp.firm.trialEndsAt).getTime() > Date.now();
      }
      return false;
    }
    try {
      // Create the firm + backfill user profile fields. The endpoint is
      // idempotent for existing-firm users (e.g. an existing user
      // resuming the wizard to fill missing firstName/phone): it
      // backfills the user row and returns the existing firm rather
      // than creating a duplicate.
      const resp = await apiFetch<FirmsResp>('/api/firms', { method: 'POST', body });
      if (alreadyPaying(resp)) {
        // Existing-firm user just completed their profile — no need
        // to charge them again. Land on the dashboard.
        onSkipPayment();
      } else if (paymentConfigured) {
        onNext();
      } else {
        onSkipPayment();
      }
    } catch (err) {
      if (err instanceof ApiError && err.code === 'slug_collision_retry') {
        // Race; one quick retry.
        try {
          const resp = await apiFetch<FirmsResp>('/api/firms', { method: 'POST', body });
          if (alreadyPaying(resp)) onSkipPayment();
          else if (paymentConfigured) onNext();
          else onSkipPayment();
          return;
        } catch {
          // fall through
        }
      }
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="reg-step reg-done">
      <div className="reg-success-circle" aria-hidden="true">✓</div>
      <h2 className="reg-step-title centered">{t('step_done.title')}</h2>
      <p className="reg-step-sub centered">{t('step_done.subtitle')}</p>

      <div className="reg-summary">
        <div className="reg-summary-row">
          <span className="reg-sl">{t('step_done.summary.name')}</span>
          <span className="reg-sv">
            {[data.firstName, data.lastName].filter(Boolean).join(' ') || '—'}
          </span>
        </div>
        <div className="reg-summary-row">
          <span className="reg-sl">{t('step_done.summary.firm')}</span>
          <span className="reg-sv">{data.firmName || '—'}</span>
        </div>
        <div className="reg-summary-row">
          <span className="reg-sl">{t('step_done.summary.email')}</span>
          <span className="reg-sv">{data.email || '—'}</span>
        </div>
        <div className="reg-summary-row">
          <span className="reg-sl">{t('step_done.summary.status')}</span>
          <span className="reg-sv">
            <span className="reg-badge-trial">{t('step_done.summary.status_value')}</span>
          </span>
        </div>
      </div>

      {error && <div className="reg-error">{error}</div>}

      <button type="button" className="btn-primary reg-cta" onClick={handleContinue} disabled={busy}>
        {busy ? '…' : t('step_done.submit')}
      </button>
      <p className="reg-support">
        {t('step_done.support_prompt')}{' '}
        <a href={`mailto:${t('step_done.support_link')}`}>{t('step_done.support_link')}</a>
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------ Step 3 */

function PaymentNotConfigured({ onContinue }: { onContinue: () => void }) {
  const { t } = useTranslation('register');
  return (
    <div className="reg-step">
      <h2 className="reg-step-title">{t('step_payment.title')}</h2>
      <p className="reg-step-sub">{t('step_payment.errors.not_configured')}</p>
      <button type="button" className="btn-primary reg-cta" onClick={onContinue}>
        {t('step_receipt.enter_app')}
      </button>
    </div>
  );
}

function StepPayment({ data, onDone }: { data: WizardData; onDone: () => void }) {
  const { t } = useTranslation('register');
  const stripe = useStripe();
  const elements = useElements();
  const [plan, setPlan] = useState<Plan>('annual');
  const [cardName, setCardName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [phase, setPhase] = useState<'form' | 'processing' | 'receipt'>('form');
  const [processingStep, setProcessingStep] = useState<number>(0);
  const [receipt, setReceipt] = useState<{
    txn: string;
    plan: Plan;
    last4: string;
    chargeDate: string;
    total: string;
  } | null>(null);

  const heMonths = [
    'ינואר','פברואר','מרץ','אפריל','מאי','יוני',
    'יולי','אוגוסט','ספטמבר','אוקטובר','נובמבר','דצמבר',
  ];
  function formatHebDate(d: Date): string {
    return `${d.getDate()} ב${heMonths[d.getMonth()]} ${d.getFullYear()}`;
  }
  const trialEnd = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d;
  }, []);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!stripe || !elements) return;
    if (!cardName.trim()) return setError(t('step_payment.errors.no_name'));

    setBusy(true);
    setError(null);
    setPhase('processing');
    setProcessingStep(0);

    try {
      // 1. Server creates SetupIntent + Customer.
      const intent = await apiFetch<{ clientSecret: string; customerId: string }>(
        '/api/billing/setup-intent',
        { method: 'POST', body: JSON.stringify({}) },
      );
      setProcessingStep(1);

      // 2. Confirm card on the client. Stripe handles 3DS if required.
      const card = elements.getElement(CardElement);
      if (!card) throw new Error('card_element_missing');
      const { error: stripeErr, setupIntent } = await stripe.confirmCardSetup(
        intent.clientSecret,
        {
          payment_method: {
            card,
            billing_details: { name: cardName.trim(), email: data.email.trim() },
          },
        },
      );
      if (stripeErr) {
        setPhase('form');
        return setError(stripeErr.message ?? t('step_payment.errors.stripe_error'));
      }
      if (!setupIntent || setupIntent.status !== 'succeeded') {
        setPhase('form');
        return setError(t('step_payment.errors.stripe_error'));
      }
      setProcessingStep(2);

      const paymentMethodId =
        typeof setupIntent.payment_method === 'string'
          ? setupIntent.payment_method
          : (setupIntent.payment_method?.id ?? undefined);

      // 3. Server sets the card as default + creates the Subscription
      // (trial_period_days: 30) so the day-30 charge has a PM to bill.
      const sub = await apiFetch<{ subscriptionId: string; status: string; plan: Plan }>(
        '/api/billing/subscribe',
        { method: 'POST', body: JSON.stringify({ plan, paymentMethodId }) },
      );
      setProcessingStep(3);

      // 4. Fetch the actual card details for the receipt. /api/billing/
      // payment-method returns brand + last4 from Stripe; if it 502s or
      // returns null, we fall back to a placeholder rather than blocking.
      let last4 = '••••';
      try {
        const pm = await apiFetch<{
          paymentMethod: { brand: string; last4: string } | null;
        }>('/api/billing/payment-method');
        if (pm.paymentMethod) last4 = pm.paymentMethod.last4;
      } catch {
        /* keep placeholder */
      }
      setReceipt({
        txn: sub.subscriptionId,
        plan: sub.plan,
        last4,
        chargeDate: formatHebDate(trialEnd),
        total: plan === 'annual' ? '₪1,000' : '₪100',
      });
      setPhase('receipt');
    } catch (err) {
      setPhase('form');
      setError(
        err instanceof ApiError
          ? t(`step_payment.errors.${err.code}`, {
              defaultValue: t('step_payment.errors.stripe_error'),
            })
          : t('step_payment.errors.stripe_error'),
      );
    } finally {
      setBusy(false);
    }
  }

  if (phase === 'processing') {
    return <PaymentProcessing step={processingStep} />;
  }
  if (phase === 'receipt' && receipt) {
    return (
      <PaymentReceipt
        receipt={receipt}
        formatHebDate={formatHebDate}
        onEnter={onDone}
      />
    );
  }

  return (
    <form className="reg-step" onSubmit={handleSubmit}>
      <h2 className="reg-step-title">{t('step_payment.title')}</h2>
      <p className="reg-step-sub">{t('step_payment.subtitle')}</p>

      <div className="reg-plan-grid">
        <PlanCard
          plan="monthly"
          selected={plan === 'monthly'}
          onSelect={() => setPlan('monthly')}
        />
        <PlanCard
          plan="annual"
          selected={plan === 'annual'}
          onSelect={() => setPlan('annual')}
        />
      </div>

      <div className="reg-order-summary">
        <div className="reg-os-title">{t('step_payment.summary.title')}</div>
        <div className="reg-os-row">
          <span className="reg-os-l">{t('step_payment.summary.plan')}</span>
          <span className="reg-os-v">
            {plan === 'annual' ? t('step_payment.plan_annual') : t('step_payment.plan_monthly')}
          </span>
        </div>
        <div className="reg-os-row">
          <span className="reg-os-l">{t('step_payment.summary.trial')}</span>
          <span className="reg-os-v reg-os-trial">{t('step_payment.summary.trial_value')}</span>
        </div>
        <div className="reg-os-row">
          <span className="reg-os-l">{t('step_payment.summary.price')}</span>
          <span className="reg-os-v">{plan === 'annual' ? '₪1,000' : '₪100'}</span>
        </div>
        <div className="reg-os-row">
          <span className="reg-os-l">{t('step_payment.summary.first_charge')}</span>
          <span className="reg-os-v">{formatHebDate(trialEnd)}</span>
        </div>
        <div className="reg-os-row reg-os-total">
          <span className="reg-os-l">{t('step_payment.summary.after_trial')}</span>
          <span className="reg-os-v">
            {plan === 'annual'
              ? t('step_payment.summary.annual_total', { amount: '₪1,000' })
              : t('step_payment.summary.monthly_total', { amount: '₪100' })}
          </span>
        </div>
      </div>

      <label className="reg-field">
        <span>
          {t('step_payment.card_name')}
          {t('step_details.required_marker')}
        </span>
        <input
          type="text"
          value={cardName}
          onChange={(e) => setCardName(e.target.value)}
          placeholder={t('step_payment.card_name_placeholder')}
          autoComplete="cc-name"
          required
        />
      </label>

      <label className="reg-field">
        <span>
          {t('step_payment.card_field')}
          {t('step_details.required_marker')}
        </span>
        <div className="reg-card-element">
          <CardElement
            options={{
              hidePostalCode: true,
              style: {
                base: {
                  fontSize: '14px',
                  color: 'var(--color-text-primary)',
                  fontFamily: 'inherit',
                  '::placeholder': { color: 'var(--color-text-secondary)' },
                },
              },
            }}
          />
        </div>
      </label>

      <div className="reg-secure-row" aria-hidden="true">
        🔒 {t('step_payment.secure_row')}
      </div>

      {error && <div className="reg-error">{error}</div>}

      <button type="submit" className="btn-primary reg-cta" disabled={busy || !stripe}>
        {busy ? t('step_payment.submit_busy') : t('step_payment.submit')}
      </button>
      <p className="reg-footnote">{t('step_payment.footnote')}</p>
    </form>
  );
}

function PlanCard({
  plan,
  selected,
  onSelect,
}: {
  plan: Plan;
  selected: boolean;
  onSelect: () => void;
}) {
  const { t } = useTranslation('register');
  const isAnnual = plan === 'annual';
  return (
    <button
      type="button"
      className={`reg-plan-card ${selected ? 'selected' : ''}`}
      onClick={onSelect}
      aria-pressed={selected}
    >
      <div className="reg-plan-radio">
        <div className="reg-plan-radio-dot" />
      </div>
      <div
        className={`reg-plan-badge ${isAnnual ? 'pb-save' : 'pb-monthly'}`}
      >
        {isAnnual
          ? t('step_payment.plan_annual_badge')
          : t('step_payment.plan_monthly_badge')}
      </div>
      <div className="reg-plan-name">
        {isAnnual ? t('step_payment.plan_annual') : t('step_payment.plan_monthly')}
      </div>
      <div className="reg-plan-price">{isAnnual ? '₪1,000' : '₪100'}</div>
      <div className="reg-plan-per">
        {isAnnual ? t('step_payment.plan_annual_period') : t('step_payment.plan_monthly_period')}
      </div>
      <div className="reg-plan-features">
        {(isAnnual
          ? ['annual_save', 'annual_priority', 'all_features']
          : ['monthly_flex', 'monthly_cancel', 'all_features']
        ).map((k) => (
          <div className="reg-pf" key={k}>
            ✓ {t(`step_payment.plan_features.${k}`)}
          </div>
        ))}
      </div>
    </button>
  );
}

function PaymentProcessing({ step }: { step: number }) {
  const { t } = useTranslation('register');
  const labels = ['validating', 'connecting', 'confirming', 'activating'] as const;
  return (
    <div className="reg-processing">
      <div className="reg-spinner" />
      <div className="reg-proc-text">{t(`step_payment.processing.${labels[step] ?? 'activating'}`)}</div>
      <div className="reg-proc-steps">
        {labels.map((k, i) => (
          <div
            key={k}
            className={`reg-pstep ${i < step ? 'done' : i === step ? 'active' : ''}`}
          >
            <span className="reg-ps-dot" />
            {t(`step_payment.processing.${k}`)}
          </div>
        ))}
      </div>
    </div>
  );
}

function PaymentReceipt({
  receipt,
  formatHebDate,
  onEnter,
}: {
  receipt: {
    txn: string;
    plan: Plan;
    last4: string;
    chargeDate: string;
    total: string;
  };
  formatHebDate: (d: Date) => string;
  onEnter: () => void;
}) {
  const { t } = useTranslation('register');
  const today = new Date();
  return (
    <div className="reg-receipt">
      <div className="reg-receipt-header">
        <div className="reg-receipt-icon" aria-hidden="true">✓</div>
        <div className="reg-receipt-title">{t('step_receipt.title')}</div>
        <div className="reg-receipt-sub">
          {receipt.plan === 'annual'
            ? t('step_receipt.sub_annual')
            : t('step_receipt.sub_monthly')}
        </div>
      </div>
      <div className="reg-receipt-body">
        <ReceiptRow label={t('step_receipt.fields.txn')} value={receipt.txn} />
        <ReceiptRow label={t('step_receipt.fields.date')} value={formatHebDate(today)} />
        <ReceiptRow
          label={t('step_receipt.fields.plan')}
          value={
            receipt.plan === 'annual'
              ? t('step_payment.plan_annual')
              : t('step_payment.plan_monthly')
          }
        />
        <ReceiptRow label={t('step_receipt.fields.card')} value={`•••• ${receipt.last4}`} />
        <ReceiptRow
          label={t('step_receipt.fields.trial')}
          value={t('step_receipt.fields.trial_value')}
          accent="green"
        />
        <ReceiptRow label={t('step_receipt.fields.first_charge')} value={receipt.chargeDate} />
        <ReceiptRow label={t('step_receipt.fields.total')} value={receipt.total} accent="big" />
      </div>
      <div className="reg-receipt-footer">
        <p className="reg-rf-note">{t('step_receipt.footer')}</p>
        <button type="button" className="btn-primary reg-cta" onClick={onEnter}>
          {t('step_receipt.enter_app')}
        </button>
      </div>
    </div>
  );
}

function ReceiptRow({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent?: 'green' | 'big';
}) {
  return (
    <div className="reg-rb-row">
      <span className="reg-rb-label">{label}</span>
      <span className={`reg-rb-val ${accent === 'green' ? 'green' : ''} ${accent === 'big' ? 'big' : ''}`}>
        {value}
      </span>
    </div>
  );
}
